import { Center, Loader } from "@mantine/core";
import { useDisclosure, useListState } from "@mantine/hooks";
import { useTranslation } from "react-i18next";

import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import { useUpdateCluster } from "#modules/similarPart/infra/clustering/useUpdateCluster";
import { useSearchReliables } from "#modules/similarPart/infra/useSearchReliables";
import { ResultToolbar } from "#modules/similarPart/view/ResultToolbar";
import { TargetDto } from "#modules/similarPart/view/SearchResult";
import { Box } from "#shared/view/components/Box";
import { Group } from "#shared/view/components/Group";
import { Modal } from "#shared/view/components/Modal";
import { Space } from "#shared/view/components/Space";
import { Text } from "#shared/view/components/Text";
import { Button } from "#shared/view/designSystem/Button";
import { FilterQuery } from "shared";

import { SimilarPartCard } from "./SimilarPartCard";
import { usePredictProcessSteps } from "./usePredictProcessSteps";

type Props = {
  quoteRequestLine: QuoteRequestLineDto;
  opened: boolean;
  onClose: () => void;
  filtersParams: FilterQuery;
  setFiltersParams: (params: FilterQuery) => void;
  dimensionWeight: number;
  setDimensionWeight: (weight: number) => void;
};

export const SimilarPartsModal = ({
  quoteRequestLine,
  opened,
  onClose,
  filtersParams,
  setFiltersParams,
  dimensionWeight,
  setDimensionWeight,
}: Props) => {
  const { t } = useTranslation();
  const [displayFeatures, { toggle: toggleDisplayFeatures }] = useDisclosure(false);
  const [displayScore, { toggle: toggleDisplayScore }] = useDisclosure(false);

  const { similarParts, isLoading, refetch } = useSearchReliables(
    {
      id: quoteRequestLine.id,
      filtersParams,
      dimensionWeight,
    },
    {
      enabled: opened,
    },
  );

  const reliableSimilarParts = similarParts || [];

  const targets: TargetDto[] = quoteRequestLine.cluster?.targetQuoteRequestLines ?? [];

  const [cluster, { append, remove, pop }] = useListState(
    targets.map((target) => ({ id: target.id })),
  );

  const isSelected = (qrl: Pick<QuoteRequestLineDto, "id"> | undefined) => {
    if (!qrl) {
      return false;
    }
    return cluster.some((selectedQrl) => selectedQrl.id === qrl.id);
  };

  const handleToggleSelect = (qrl: Pick<QuoteRequestLineDto, "id">, isMultiSelect = false) => {
    if (isMultiSelect) {
      if (isSelected(qrl)) {
        remove(cluster.findIndex((c) => c.id === qrl.id));
      } else {
        append(qrl);
      }
    } else {
      pop();
      append(qrl);
    }
  };

  const { predictProcessSteps } = usePredictProcessSteps({
    onSuccess: () => {
      onClose();
    },
  });

  const { updateCluster, isPending } = useUpdateCluster({
    onSuccess: async (updatedQuoteRequestLine) => {
      if (!updatedQuoteRequestLine) {
        return;
      }

      await predictProcessSteps({
        quoteRequestLineId: quoteRequestLine.id,
        shouldHandleAssembly: true,
        failedOrUnStartedChildrenOnly: false,
      });
    },
  });

  const createCluster = () => {
    updateCluster({
      sourceId: quoteRequestLine.id,
      cluster: cluster.map((target) => target.id),
    });
  };

  return (
    <Modal opened={opened} onClose={onClose} size="xl">
      <ResultToolbar
        filtersParams={filtersParams}
        setFiltersParams={setFiltersParams}
        onSearch={refetch}
        part={quoteRequestLine.part}
        title={t("similarPart.similar_parts")}
        toggleDisplayFeatures={toggleDisplayFeatures}
        displayFeatures={displayFeatures}
        toggleDisplayScore={toggleDisplayScore}
        displayScore={displayScore}
        dimensionWeight={dimensionWeight}
        setDimensionWeight={setDimensionWeight}
      />
      <Space h="md" />

      {isLoading ? (
        <Center>
          <Loader />
        </Center>
      ) : (
        <>
          {reliableSimilarParts.length === 0 && (
            <Text fz="lg" c="dimmed">
              {t("qrl.prediction.reliable.emptyState")}
            </Text>
          )}
          <Group>
            {reliableSimilarParts.map((similarPart) =>
              similarPart.quoteRequestLines.map((qrl, index) => (
                <Box w={200} key={qrl.id}>
                  <SimilarPartCard
                    quoteRequestLineId={qrl.id}
                    sourcePart={quoteRequestLine.part}
                    isSelected={isSelected(qrl)}
                    handleToggleSelect={() => handleToggleSelect(qrl)}
                    index={index}
                  />
                </Box>
              )),
            )}
          </Group>
        </>
      )}
      <Space h="sm" />
      <Group justify="end">
        <Button onClick={createCluster} loading={isPending} disabled={cluster.length === 0}>
          {t("qrl.prediction.reliable.button")}
        </Button>
      </Group>
    </Modal>
  );
};
